/**
 * Reward pops (fields and dungeons, one system): "+48 XP", "+12 gold", "Rare gear!" rise from what
 * paid them with an arc and a bounce, then fly into the HUD (XP bar, gold counter, bag) where they
 * land with a pulse and a tick sound. Pure timeline + merging; hud/field/rewardPops.ts draws it.
 *
 *  - kinds stagger (XP, then gold, then items) so one kill's receipt never overlaps itself;
 *  - pops of a kind born within MERGE_MS merge into one tally ("+312 XP ×7"), so a big AoE pull
 *    reads as one fat number instead of a wall of small ones;
 *  - at most MAX_POPS fly at once (the oldest of the same kind absorbs the rest).
 */

export type RewardKind = 'xp' | 'gold' | 'item';
export type RewardRarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

export interface RewardPart {
  kind: RewardKind;
  /** XP / gold amount (items: 1 per item). */
  amount: number;
  /** Items: the drop's rarity (colour, beam, sound). */
  rarity?: RewardRarity;
  /** Items: a name when known ("Rare gear" otherwise). */
  label?: string;
}

export interface RewardPop {
  id: number;
  kind: RewardKind;
  amount: number;
  /** Kills / pickups merged into it (×N when > 1). */
  count: number;
  rarity: RewardRarity;
  label: string | null;
  /** World position it rises from (sim yards). */
  wx: number;
  wy: number;
  /** When it appears (after its stagger) and when it was created. */
  bornAt: number;
  createdAt: number;
  /** Set by the view once it lands. */
  landed: boolean;
}

/** Merge window, stagger per kind, timeline (ms). */
export const MERGE_MS = 320;
export const STAGGER_MS: Readonly<Record<RewardKind, number>> = { xp: 0, gold: 110, item: 230 };
export const RISE_MS = 520;
export const HOLD_MS = 200;
export const FLY_MS = 430;
export const POP_LIFE_MS = RISE_MS + HOLD_MS + FLY_MS;
export const MAX_POPS = 10;

const RANK: readonly RewardRarity[] = ['common', 'uncommon', 'rare', 'epic', 'legendary'];
const better = (a: RewardRarity, b: RewardRarity): RewardRarity => (RANK.indexOf(a) >= RANK.indexOf(b) ? a : b);

export class RewardPopModel {
  readonly pops: RewardPop[] = [];
  private seq = 0;

  /** Rewards from one source at world `pos` (a kill, a pickup, a chest). */
  add(now: number, pos: { x: number; y: number }, parts: readonly RewardPart[]): void {
    for (const p of parts) {
      if (!(p.amount > 0)) continue;
      const rarity = p.rarity ?? (p.kind === 'item' ? 'uncommon' : 'common');
      // Merge: same kind, still young (not yet visibly flying), and items only of the same rarity.
      const into = this.pops.find((q) => q.kind === p.kind && !q.landed && now - q.createdAt <= MERGE_MS && (p.kind !== 'item' || q.rarity === rarity));
      if (into) {
        into.amount += p.amount;
        into.count += 1;
        into.rarity = better(into.rarity, rarity);
        if (p.label) into.label = into.count > 1 ? null : p.label;
        continue;
      }
      if (this.pops.length >= MAX_POPS) {
        const old = this.pops.find((q) => q.kind === p.kind && !q.landed);
        if (old) {
          old.amount += p.amount;
          old.count += 1;
          old.rarity = better(old.rarity, rarity);
          continue;
        }
      }
      this.pops.push({ id: ++this.seq, kind: p.kind, amount: p.amount, count: 1, rarity, label: p.label ?? null, wx: pos.x, wy: pos.y, bornAt: now + STAGGER_MS[p.kind], createdAt: now, landed: false });
    }
  }

  /** Drop finished pops (call once per frame). */
  prune(now: number): void {
    for (let i = this.pops.length - 1; i >= 0; i--) if (now - this.pops[i]!.bornAt > POP_LIFE_MS + 60) this.pops.splice(i, 1);
  }

  clear(): void {
    this.pops.length = 0;
  }
}

/** Where a pop is in its life: waiting (stagger), rising at its world spot, holding, flying to the HUD, done. */
export function popPhase(p: RewardPop, now: number): { phase: 'wait' | 'rise' | 'hold' | 'fly' | 'done'; k: number } {
  const t = now - p.bornAt;
  if (t < 0) return { phase: 'wait', k: 0 };
  if (t < RISE_MS) return { phase: 'rise', k: t / RISE_MS };
  if (t < RISE_MS + HOLD_MS) return { phase: 'hold', k: (t - RISE_MS) / HOLD_MS };
  if (t < POP_LIFE_MS) return { phase: 'fly', k: (t - RISE_MS - HOLD_MS) / FLY_MS };
  return { phase: 'done', k: 1 };
}

/** The pop's text ("+312 XP ×7", "+12 gold", "Rare gear!"). */
export function popText(p: RewardPop): { main: string; tail: string } {
  const n = p.count > 1 ? ` ×${p.count}` : '';
  if (p.kind === 'xp') return { main: `+${Math.round(p.amount).toLocaleString('en-US')}`, tail: ` XP${n}` };
  if (p.kind === 'gold') return { main: `+${Math.round(p.amount).toLocaleString('en-US')}`, tail: ` gold${n}` };
  const r = p.rarity[0]!.toUpperCase() + p.rarity.slice(1);
  return { main: p.label && p.count === 1 ? p.label : `${r} gear`, tail: p.amount > 1 ? ` ×${p.amount}` : '!' };
}

/** Rise curve: an arc up (px) and a scale bounce (0.55 → 1.25 → 1). */
export function riseShape(k: number): { up: number; scale: number } {
  const e = 1 - (1 - k) ** 3;
  const scale = k < 0.35 ? 0.55 + (1.25 - 0.55) * (k / 0.35) : 1.25 - 0.25 * Math.min(1, (k - 0.35) / 0.4);
  return { up: 46 * e, scale };
}
